import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bug, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/lib/api/client';

export function BugReportSection() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Show on Genius pages (path starts with /genius/)
  if (!location.pathname.startsWith('/genius/')) return null;

  const handleSubmit = async () => {
    if (description.trim().length < 10) {
      toast.error('Please describe the bug in at least 10 characters');
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient.post('/bug-reports', {
        description: description.trim(),
        page_url: window.location.href,
      });
      toast.success('Bug report submitted — thank you!');
      setDescription('');
      setOpen(false);
    } catch {
      toast.error('Failed to submit bug report');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="border-t border-slate-200 bg-slate-50/50 py-4 mt-8">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="flex items-center gap-2 text-xs text-muted-foreground hover:text-slate-700 transition-colors">
              <Bug className="h-3.5 w-3.5" />
              Found a bug? Report it here
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bug className="h-5 w-5" />
                Report a Bug
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  Describe what happened and what you expected. We'll look into it right away.
                </p>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What went wrong? What were you trying to do?"
                  className="resize-none h-32"
                  maxLength={2000}
                />
                <p className="text-xs text-muted-foreground text-right mt-1">{description.length}/2000</p>
              </div>
              <p className="text-xs text-muted-foreground">
                Page: {typeof window !== 'undefined' ? window.location.pathname : ''}
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSubmit}
                  disabled={isSubmitting || description.trim().length < 10}
                >
                  {isSubmitting ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Send className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  {isSubmitting ? 'Submitting...' : 'Submit Report'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
